import {
	fetchMargin,
	fetchPostTradeDetails,
	fetchShouldUpdateKeeperFee
} from '$lib/apis/userInteract';
import {
	currentMarket,
	loadingPostTradeDetails,
	postTradeDetails,
	tokensAllowedForDepositBalance
} from '$lib/home/store';
import {
	adjustMarginMarket,
	executionType,
	input, isTPSLActive,
	LoadingAccessibleMargin,
	longOrShort,
	marginInputForTrade,
	triggerInputForTrade,
	userMarginList
} from '$lib/home/userInteract/store';
import {
	closePositionInput,
	closePositionPostTradeDetails,
	currentClosePosition,
	loadingClosePositionPostTradeDetails, stopLossInput, takeProfitInput
} from '$lib/popups/popupsStore';
import type { MarketIdentifiers } from '$lib/types/market';
import type { UserMargin } from '$lib/types/user';
import type { PostTradeDetails, ShouldUpdateKeeperFee } from '$lib/types/userInteract';
import BigNumber from 'bignumber.js';
import { get } from 'svelte/store';
import { findRequiredCallData } from './matchaCallData';
import { AUTOSWAP_OP, AUTOSWAP_STABLES } from '$lib/settings';
import { getEstimatedTradingReward, getEstimatedTradingRewardClosePosition } from '$lib/data/rewards';

export async function getMargin() {
	// if adjust margin popup is open, then we update the margin for that market
	const currAdjustMarginMarket = get(adjustMarginMarket);
	if (currAdjustMarginMarket) await getMarginForMarket(currAdjustMarginMarket);
	// if not open then we update the margin for the current market
	const currMarket = get(currentMarket);
	if (currMarket == undefined) return;
	await getMarginForMarket(currMarket?.marketIdentifier);
}

export async function getMarginForMarket(marketId: MarketIdentifiers) {
	LoadingAccessibleMargin.set(true);
	const data: UserMargin = (await fetchMargin(marketId)) as UserMargin;

	const currentUserMargin = get(userMarginList);
	let tempUserMargin: UserMargin;
	if (data) {
		tempUserMargin = data;
	} else {
		tempUserMargin = {
			asset: '',
			marketAddress: '',
			marketIdentifier: '',
			depositedMargin: '',
			accessibleMargin: '',
			maxBuyingPower: '',
			utilizedBuyingPower: '',
			remainingBuyingPower: '',
			currentLeverage: '',
			maxLeverage: ''
		};
	}

	if (currentUserMargin) {
		const index = currentUserMargin.findIndex((margin) => margin.asset == tempUserMargin.asset);
		if (index != -1) {
			currentUserMargin[index] = tempUserMargin;
		} else {
			currentUserMargin.push(tempUserMargin);
		}
	}

	userMarginList.set(currentUserMargin);

	LoadingAccessibleMargin.set(false);
}

export async function getPostTradeDetails() {
	const currentMarketToTrade = get(currentMarket);
	if (currentMarketToTrade?.currentMarketPrice && !['', '0'].includes(get(input))) {
		let inputInAsset = new BigNumber(get(input))
			.div(currentMarketToTrade.currentMarketPrice)
			.multipliedBy(1e18)
			.multipliedBy(get(longOrShort) == 'Long' ? 1 : -1)
			.decimalPlaces(0)
			.toFixed();

		loadingPostTradeDetails.set(true);
		const data: PostTradeDetails = (await fetchPostTradeDetails(
			inputInAsset,
			currentMarketToTrade.marketIdentifier,
			get(marginInputForTrade),
			get(executionType) != 'Market' ? get(triggerInputForTrade) : undefined
		)) as PostTradeDetails;
		loadingPostTradeDetails.set(false);
		postTradeDetails.set(data);

		// call estimated rewards asynchronously

		let isTPSLOrder =
			get(isTPSLActive) && (get(takeProfitInput) != '0' || get(stopLossInput) != '0');
		let fee = '0';
		if (isTPSLOrder) {
			fee = new BigNumber(data.fee).multipliedBy(2).toString();
		} else {
			fee = data.fee;
		}

		getEstimatedTradingReward(fee);
		if (get(AUTOSWAP_STABLES) || get(AUTOSWAP_OP)) {
			findRequiredCallData(
				get(tokensAllowedForDepositBalance),
				new BigNumber(get(marginInputForTrade))
			);
		}
	}
}

export async function getClosePositionPostTradeDetails() {
	let closePosition = get(currentClosePosition);
	if (get(currentMarket) != undefined && closePosition != undefined) {
		let inputInAsset = new BigNumber(get(closePositionInput))
			.multipliedBy(new BigNumber(closePosition.size).gte(0) ? -1 : 1)
			.decimalPlaces(0)
			.toFixed();

		loadingClosePositionPostTradeDetails.set(true);
		const data: PostTradeDetails = (await fetchPostTradeDetails(
			inputInAsset,
			closePosition.marketIdentifier,
			'0'
		)) as PostTradeDetails;
		loadingClosePositionPostTradeDetails.set(false);
		closePositionPostTradeDetails.set(data);

		// call estimated rewards asynchronously
		getEstimatedTradingRewardClosePosition(data.fee);
	}
}

export async function getShouldUpdateKeeperFee() {
	const data = (await fetchShouldUpdateKeeperFee()) as ShouldUpdateKeeperFee;
	if (data) {
		return data.shouldUpdate;
	} else {
		return false;
	}
}
